import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiFileText, FiDownload, FiPhone, FiAlertCircle } from 'react-icons/fi';
import Navbar from './Navbar';

const API_BASE_URL = 'https://infrec.in/php-backend/backend/user';

const MyDashboard = () => {
  const [customer, setCustomer] = useState(null);
  const [purchases, setPurchases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        const customerDataStr = sessionStorage.getItem('customerData');
        if (!customerDataStr) {
          setError('Customer data not found. Please login again.');
          navigate('/customer-login');
          return;
        }

        const customerData = JSON.parse(customerDataStr);
        if (!customerData || !customerData.id) {
          setError('Invalid customer data. Please login again.');
          navigate('/customer-login');
          return;
        }

        setCustomer(customerData);

        try {
          const response = await fetch(`${API_BASE_URL}/customerprofile.php?customer_id=${customerData.id}`);
          
          if (!response.ok) {
            console.error('API Error:', response.status, response.statusText);
            setPurchases([]);
            return;
          }

          const text = await response.text();
          console.log('API Response:', text);
          
          if (!text) {
            console.error('Empty response from API');
            setPurchases([]);
            return;
          }

          const data = JSON.parse(text);

          if (data.success) {
            console.log('Purchases data:', data.purchases);
            setPurchases(data.purchases || []);
          } else {
            console.error('API Error:', data.message);
            setPurchases([]);
          }
        } catch (apiErr) {
          console.error('API Fetch Error:', apiErr);
          setPurchases([]);
        }
      } catch (err) {
        console.error('Error fetching dashboard data:', err);
        setError('Error loading dashboard. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, [navigate]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading your dashboard...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="bg-white rounded-lg shadow-md p-8 max-w-md text-center">
          <p className="text-red-600 font-semibold mb-4">{error}</p>
          <button
            onClick={() => navigate('/customer-login')}
            className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700"
          >
            Go to Login
          </button>
        </div>
      </div>
    );
  }

  if (!customer) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <p className="text-gray-600">No customer data available</p>
      </div>
    );
  }

  // Calculate statistics
  const totalPremium = purchases.reduce((sum, p) => sum + parseFloat(p.amount || 0), 0);
  const activePolicies = purchases.filter(p => {
    if (!p.status) return false;
    const status = p.status.trim().toLowerCase();
    return status === 'active';
  }).length;
  console.log('Total Purchases:', purchases.length);
  console.log('Active Policies:', activePolicies);
  console.log('All Statuses:', purchases.map(p => p.status));
  const upcomingRenewals = purchases.filter(p => {
    const renewalDate = new Date(p.renewal_date);
    const today = new Date();
    return renewalDate > today && renewalDate <= new Date(today.getTime() + 90 * 24 * 60 * 60 * 1000);
  }).length;

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString('en-IN');
    } catch {
      return dateString;
    }
  };

  const formatCurrency = (amount) => {
    if (!amount) return '₹0.00';
    return '₹' + parseFloat(amount).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar customerName={customer.name} />

      <div className="py-8 px-4 md:px-8">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="mb-8 pt-12">
            <h1 className="text-4xl font-bold text-gray-900 mb-2">My Dashboard</h1>
            <p className="text-gray-600">Welcome back, {customer.name}</p>
          </div>

          {/* Quick Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            <div className="bg-white rounded-lg shadow-md p-6 border-t-4" style={{borderColor: '#305daa'}}>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-600 text-sm font-semibold uppercase">Total Premium Paid</p>
                  <p className="text-3xl font-bold mt-2" style={{color: '#305daa'}}>{formatCurrency(totalPremium)}</p>
                </div>
                <div className="text-4xl">💳</div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-md p-6 border-t-4" style={{borderColor: '#305daa'}}>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-600 text-sm font-semibold uppercase">Active Policies</p>
                  <p className="text-3xl font-bold mt-2" style={{color: '#305daa'}}>{activePolicies || 0}</p>
                  {activePolicies === 0 && purchases.length > 0 && (
                    <p className="text-xs text-gray-500 mt-1">Check status values</p>
                  )}
                </div>
                <div className="text-4xl">✅</div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-md p-6 border-t-4" style={{borderColor: '#305daa'}}>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-600 text-sm font-semibold uppercase">Upcoming Renewals</p>
                  <p className="text-3xl font-bold mt-2" style={{color: '#305daa'}}>{upcomingRenewals}</p>
                </div>
                <div className="text-4xl">📅</div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-md p-6 border-t-4" style={{borderColor: '#305daa'}}>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-600 text-sm font-semibold uppercase">Total Policies</p>
                  <p className="text-3xl font-bold mt-2" style={{color: '#305daa'}}>{purchases.length}</p>
                </div>
                <div className="text-4xl">📋</div>
              </div>
            </div>
          </div>

          {/* All Policies Table - Complete Columns */}
          <div className="bg-white rounded-lg shadow-md p-8 mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-6" style={{color: '#305daa'}}>My Policies</h2>
            {purchases.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b-2" style={{borderColor: '#305daa'}}>
                      <th className="text-left py-3 px-3 font-semibold text-gray-700">Policy Number</th>
                      <th className="text-left py-3 px-3 font-semibold text-gray-700">Product Name</th>
                      <th className="text-left py-3 px-3 font-semibold text-gray-700">Category</th>
                      <th className="text-left py-3 px-3 font-semibold text-gray-700">Subcategory</th>
                      <th className="text-left py-3 px-3 font-semibold text-gray-700">Sum Assured</th>
                      <th className="text-left py-3 px-3 font-semibold text-gray-700">Annual Premium</th>
                      <th className="text-left py-3 px-3 font-semibold text-gray-700">Nominee</th>
                      <th className="text-left py-3 px-3 font-semibold text-gray-700">Status</th>
                      <th className="text-left py-3 px-3 font-semibold text-gray-700">Purchase Date</th>
                      <th className="text-left py-3 px-3 font-semibold text-gray-700">Renewal Date</th>
                      <th className="text-left py-3 px-3 font-semibold text-gray-700">Maturity Date</th>
                      <th className="text-left py-3 px-3 font-semibold text-gray-700">Next Payment</th>
                      <th className="text-left py-3 px-3 font-semibold text-gray-700">Frequency</th>
                    </tr>
                  </thead>
                  <tbody>
                    {purchases.map((policy) => (
                      <tr key={policy.id} className="border-b hover:bg-gray-50">
                        <td className="py-4 px-3 text-gray-900 font-semibold">{policy.policy_number}</td>
                        <td className="py-4 px-3 text-gray-900">{policy.product_name}</td>
                        <td className="py-4 px-3 text-gray-600">{policy.category_name}</td>
                        <td className="py-4 px-3 text-gray-600">{policy.subcategory_name}</td>
                        <td className="py-4 px-3 text-gray-900 font-semibold">{formatCurrency(policy.amount)}</td>
                        <td className="py-4 px-3 text-gray-900 font-semibold">{formatCurrency(policy.annual_premium)}</td>
                        <td className="py-4 px-3 text-gray-600">{policy.nominee_name || 'N/A'}</td>
                        <td className="py-4 px-3">
                          <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                            policy.status === 'Active' ? 'bg-green-100 text-green-800' :
                            policy.status === 'Inactive' ? 'bg-yellow-100 text-yellow-800' :
                            policy.status === 'Lapsed' ? 'bg-red-100 text-red-800' :
                            'bg-gray-100 text-gray-800'
                          }`}>
                            {policy.status}
                          </span>
                        </td>
                        <td className="py-4 px-3 text-gray-600">{formatDate(policy.purchase_date)}</td>
                        <td className="py-4 px-3 text-gray-600">{formatDate(policy.renewal_date)}</td>
                        <td className="py-4 px-3 text-gray-600">{formatDate(policy.maturity_date)}</td>
                        <td className="py-4 px-3 text-gray-600">{formatDate(policy.next_payment_date)}</td>
                        <td className="py-4 px-3 text-gray-600">{policy.premium_frequency || 'N/A'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-center py-8">
                <p className="text-gray-600">No policies found</p>
              </div>
            )}
          </div>

          {/* Upcoming Payments & Renewals */}
          <div className="bg-white rounded-lg shadow-md p-8 mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-6" style={{color: '#305daa'}}>Upcoming Payments & Renewals</h2>
            {purchases.length > 0 ? (
              <div className="space-y-4">
                {purchases.map((policy) => (
                  <div key={policy.id} className="border-l-4 pl-4 py-3" style={{borderColor: '#305daa'}}>
                    <div className="flex justify-between items-start">
                      <div>
                        <p className="font-semibold text-gray-900">{policy.product_name}</p>
                        <p className="text-sm text-gray-600 mt-1">Policy: {policy.policy_number}</p>
                        <p className="text-sm text-gray-600">Next Payment: {formatDate(policy.next_payment_date)} ({policy.premium_frequency})</p>
                        <p className="text-sm text-gray-600">Renewal Date: {formatDate(policy.renewal_date)}</p>
                        {policy.maturity_date && <p className="text-sm text-gray-600">Maturity Date: {formatDate(policy.maturity_date)}</p>}
                      </div>
                      <div className="text-right">
                        <p className="font-semibold text-lg" style={{color: '#305daa'}}>{formatCurrency(policy.amount)}</p>
                        <p className="text-sm text-gray-600 mt-1">{policy.category_name}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <p className="text-gray-600">No upcoming payments</p>
              </div>
            )}
          </div>

          {/* Policy Details Summary - Three Sections */}
          <div>
            <h2 className="text-2xl font-bold text-gray-900 mb-6" style={{color: '#305daa'}}>Policy Details Summary</h2>
            {purchases.length > 0 ? (
              <div className="space-y-6">
                {purchases.map((policy) => (
                  <div key={policy.id} className="bg-white rounded-lg shadow-md overflow-hidden">
                    {/* Header with Product Name */}
                    <div className="p-6 border-b-2" style={{borderColor: '#305daa', backgroundColor: '#f8f9fa'}}>
                      <h3 className="text-xl font-bold text-gray-900">{policy.product_name}</h3>
                      <p className="text-sm text-gray-600 mt-1">Policy Number: {policy.policy_number}</p>
                    </div>

                    {/* Three Sections Grid */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-0">
                      {/* Section 1: Customer Details */}
                      <div className="p-6 border-r border-gray-200">
                        <h4 className="font-bold text-gray-900 mb-4 pb-2 border-b-2" style={{borderColor: '#305daa'}}>Customer Details</h4>
                        <div className="space-y-3 text-sm">
                          <div>
                            <p className="text-gray-600 text-xs uppercase font-semibold">Category</p>
                            <p className="text-gray-900 font-medium mt-1">{policy.category_name}</p>
                          </div>
                          <div>
                            <p className="text-gray-600 text-xs uppercase font-semibold">Subcategory</p>
                            <p className="text-gray-900 font-medium mt-1">{policy.subcategory_name}</p>
                          </div>
                          <div>
                            <p className="text-gray-600 text-xs uppercase font-semibold">Nominee</p>
                            <p className="text-gray-900 font-medium mt-1">{policy.nominee_name || 'N/A'}</p>
                          </div>
                          <div>
                            <p className="text-gray-600 text-xs uppercase font-semibold">Status</p>
                            <span className={`inline-block px-3 py-1 rounded-full text-xs font-medium mt-1 ${
                              policy.status === 'Active' ? 'bg-green-100 text-green-800' :
                              policy.status === 'Inactive' ? 'bg-yellow-100 text-yellow-800' :
                              policy.status === 'Lapsed' ? 'bg-red-100 text-red-800' :
                              'bg-gray-100 text-gray-800'
                            }`}>{policy.status}</span>
                          </div>
                        </div>
                      </div>

                      {/* Section 2: Important Dates */}
                      <div className="p-6 border-r border-gray-200">
                        <h4 className="font-bold text-gray-900 mb-4 pb-2 border-b-2" style={{borderColor: '#305daa'}}>Important Dates</h4>
                        <div className="space-y-3 text-sm">
                          <div>
                            <p className="text-gray-600 text-xs uppercase font-semibold">Purchase Date</p>
                            <p className="text-gray-900 font-medium mt-1">{formatDate(policy.purchase_date)}</p>
                          </div>
                          <div>
                            <p className="text-gray-600 text-xs uppercase font-semibold">Renewal Date</p>
                            <p className="text-gray-900 font-medium mt-1">{formatDate(policy.renewal_date)}</p>
                          </div>
                          <div>
                            <p className="text-gray-600 text-xs uppercase font-semibold">Maturity Date</p>
                            <p className="text-gray-900 font-medium mt-1">{formatDate(policy.maturity_date)}</p>
                          </div>
                          <div>
                            <p className="text-gray-600 text-xs uppercase font-semibold">Next Payment</p>
                            <p className="text-gray-900 font-medium mt-1">{formatDate(policy.next_payment_date)}</p>
                          </div>
                          <div>
                            <p className="text-gray-600 text-xs uppercase font-semibold">Frequency</p>
                            <p className="text-gray-900 font-medium mt-1">{policy.premium_frequency || 'N/A'}</p>
                          </div>
                        </div>
                      </div>

                      {/* Section 3: Financial Details */}
                      <div className="p-6">
                        <h4 className="font-bold text-gray-900 mb-4 pb-2 border-b-2" style={{borderColor: '#305daa'}}>Financial Details</h4>
                        <div className="space-y-3 text-sm">
                          <div>
                            <p className="text-gray-600 text-xs uppercase font-semibold">Sum Assured</p>
                            <p className="text-lg font-bold mt-1" style={{color: '#305daa'}}>{formatCurrency(policy.amount)}</p>
                          </div>
                          <div>
                            <p className="text-gray-600 text-xs uppercase font-semibold">Annual Premium</p>
                            <p className="text-lg font-bold mt-1" style={{color: '#305daa'}}>{formatCurrency(policy.annual_premium)}</p>
                          </div>
                          <div className="pt-2 mt-2 border-t border-gray-200">
                            <p className="text-gray-600 text-xs uppercase font-semibold">Premium Breakdown</p>
                            <p className="text-sm text-gray-700 mt-2">Per {policy.premium_frequency ? policy.premium_frequency.toLowerCase() : 'month'}: <span className="font-semibold" style={{color: '#305daa'}}>{formatCurrency((parseFloat(policy.annual_premium) / (policy.premium_frequency === 'Annual' ? 1 : policy.premium_frequency === 'Semi-Annual' ? 2 : policy.premium_frequency === 'Quarterly' ? 4 : 12)))}</span></p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="bg-white rounded-lg shadow-md p-8 text-center">
                <p className="text-gray-600">No policy details available</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MyDashboard;
